import { useTranslation } from 'react-i18next'
import type { CSSProperties } from 'react'
import { Wifi, X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useSlowConnectionDetector } from '@/lib/hooks/useSlowConnectionDetector'
import { useOfflineModeStore } from '@/store/useOfflineModeStore'
import { getTopBannerOffsetPx } from '@/routes/root/view/topBannerLayout.ts'

/**
 * Banner shown when slow connection is detected and offline mode is OFF.
 * Suggests switching to offline mode for a better experience.
 */
type SlowConnectionBannerProps = {
  bannerIndex?: number
}

export default function SlowConnectionBanner({ bannerIndex = 0 }: SlowConnectionBannerProps) {
  const { t } = useTranslation()
  const { isSlowDetected, isDismissed, dismiss } = useSlowConnectionDetector()
  const isOfflineModeEnabled = useOfflineModeStore(s => s.isOfflineModeEnabled)
  const enableOfflineMode = useOfflineModeStore(s => s.enableOfflineMode)

  if (!isSlowDetected || isDismissed || isOfflineModeEnabled) return null

  const handleSwitchToOffline = () => {
    enableOfflineMode()
    dismiss()
  }

  return (
    <div
      role="alert"
      className={cn(
        'fixed left-0 right-0 z-[110]',
        'top-[calc(var(--safe-area-inset-top)+var(--top-banner-offset,0px))]',
        'md:top-[var(--top-banner-offset,0px)]',
        'md:left-[var(--sidebar-width)]',
      )}
      style={{
        '--top-banner-offset': `${getTopBannerOffsetPx({ bannerIndex })}px`,
      } as CSSProperties}
    >
      <div className="flex h-8 items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 dark:border-amber-900 dark:bg-amber-950/50">
        <Wifi className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="shrink-0 text-xs font-medium text-amber-800 dark:text-amber-200">
            {t('offlineMode.banner.slowDetected')}
          </span>
          <span className="truncate text-xs text-amber-600 dark:text-amber-400">
            {t('offlineMode.banner.slowDescription')}
          </span>
        </div>
        <button
          type="button"
          onClick={handleSwitchToOffline}
          className="shrink-0 rounded bg-amber-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600"
        >
          {t('offlineMode.banner.switchToOffline')}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 p-0.5 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
          aria-label={t('common.cancel')}
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  )
}
